import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import url from '../helpers/url';

/**
 * A hook that returns a language-aware navigation function for programmatic redirection.
 *
 * Internally calls `url.route(to)` before navigating, which:
 * - Prepends the active language segment to the path (e.g. `/ar/home`), omitting it
 *   when the active language is the application's default language.
 * - Replaces the configured default screen name with the root path `/`, so navigating
 *   to the default screen always produces a clean root URL.
 * - Preserves and encodes any query parameters passed as the second argument of
 *   `url.route` (not exposed by this hook).
 *
 * @returns A function `(to: string) => void` that accepts a screen/route name and
 *          navigates to the fully-resolved, language-prefixed URL.
 *
 * @example
 * // Active language is "ar" (Arabic, the default) — no language prefix:
 * const redirect = useRedirection();
 * redirect("home");   // navigates to /home
 * redirect("browse"); // navigates to /browse
 *
 * // Active language is a non-default (e.g. "en"):
 * redirect("home");   // navigates to /en/home
 *
 * // Navigating to the configured default screen:
 * redirect("defaultScreenName"); // navigates to /  (root)
 */
export function useRedirection() {
    const navigation = useNavigate();
    return useCallback((to: string) => navigation(url.route(to)), [navigation]);
}
